import { useQuery } from '@tanstack/react-query';

import { plenaryIdentity } from 'shared/client/utils/plenaryIdentity';
import { PLENARY_CONTEXT_ID_HEADER } from 'shared/common/consts/plenaryIdHeader';

// Tracks active blob URLs by their source URL so they can be revoked when a
// fresh fetch replaces them — not when a component unmounts.
const blobUrlMap = new Map<string, string>();

// Prevent unbounded memory growth from one-time unique src values
const MAX_BLOB_URLS = 200;

async function fetchAuthenticatedFile(url: string): Promise<string> {
  const response = await fetch(url, {
    credentials: 'include',
    ...(plenaryIdentity.contextId && { headers: { [PLENARY_CONTEXT_ID_HEADER]: plenaryIdentity.contextId } })
  });

  if (!response.ok) throw new Error(`Failed to fetch image: ${response.status}`);

  const blob = await response.blob();

  const old = blobUrlMap.get(url);
  if (old) URL.revokeObjectURL(old);

  const blobUrl = URL.createObjectURL(blob);
  blobUrlMap.set(url, blobUrl);

  // 👇 added: bounded eviction to prevent memory leak
  if (blobUrlMap.size > MAX_BLOB_URLS) {
    const oldestEntry = blobUrlMap.entries().next().value;
    if (oldestEntry) {
      const [oldestSrc, oldestBlobUrl] = oldestEntry;
      URL.revokeObjectURL(oldestBlobUrl);
      blobUrlMap.delete(oldestSrc);
    }
  }

  return blobUrl;
}

export function useFetchAuthenticatedFileQuery(src: string, options: { enabled?: boolean } = {}) {
  const { enabled = true } = options;

  return useQuery({
    queryKey: ['authenticated-file', src],
    queryFn: () => fetchAuthenticatedFile(src),
    enabled
  });
}
